import React from 'react';

import { openArtifactDisplay } from '../context/ArtifactDisplayContext';
import { resetTaskStatusFilter } from '../hooks/useTaskFilters';
import type { PlanningActionScope } from '../utils/planningActionTasks';
import { planningTaskMessage } from '../utils/planningTaskHistory';
import { planningTaskStateLabel, visiblePlanningTasks } from '../utils/planningTaskPresentation';

import { MessageIdentityLabel } from './AiOverviewPanel/MessageIdentityLabel';
import { formatOverviewDateTime } from './AiOverviewPanel/overviewDateTime';
import { usePlanningActionSelection } from './planningActionSelection';
import { PlanningTaskStageStatus } from './PlanningTaskStageStatus';
import { TaskChecklist } from './TaskChecklist';

import './PlanningActionTasks.css';

const viewAllTasks = (): void => {
  resetTaskStatusFilter();
  window.dispatchEvent(new window.CustomEvent('select_task_type', { detail: 'all' }));
  openArtifactDisplay({ tab: 'tasks', itemId: null, subview: 'list', source: 'timeline' });
};

const stateLabel = planningTaskStateLabel;

/** The same checklist selects upcoming work and follows its completion after the action runs. */
// eslint-disable-next-line max-statements, max-lines-per-function
export const PlanningActionTasks: React.FC<PlanningActionScope & { createdAt?: string }> = ({ createdAt, ...scope }) => {
  const selection = usePlanningActionSelection(scope);
  const { isLoading, active, selectedTaskIds, selectedStages, unitCount, toggle } = selection;
  const message = planningTaskMessage(selection.tasks, scope, selection.rows);
  const choices = new Map(selection.rows.map((row) => [row.id, row]));
  const visible = new Map(visiblePlanningTasks(message.rows.flatMap((row) => row.task ? [row.task] : []))
    .map((task) => [task.id, task]));
  const rows = message.rows.filter((row) => !row.task || visible.has(row.id))
    .map((row) => ({ ...choices.get(row.id), ...row, title: visible.get(row.id)?.title ?? row.title }));
  // The initial Start invocation is persisted before its title gate is
  // completed.  Its planning rows are backend state for resuming that gate,
  // not a second checklist for the transcript.  The title confirmation owns
  // this state until Start releases the tasks.
  const awaitingTitleConfirmation = rows.length > 0 && rows.every((row) => (
    row.task?.planning_metadata?.awaiting_confirmation === true
  ));

  if (!scope.projectId || !rows.length || awaitingTitleConfirmation) return null;
  const completed = rows.filter((row) => row.task?.status === 'done').length;
  const selecting = active && unitCount > 0 && !scope.historyOnly && !message.archived;
  const checklistRows = selecting ? rows.filter((row) => row.task?.status !== 'done') : rows;
  const selectedCount = selectedTaskIds.length + selectedStages.length;
  const selectionUnit = rows.some((row) => row.task?.planning_metadata?.artifact_type === 'design_page')
    ? 'page' : rows.some((row) => row.task?.planning_metadata?.artifact_type === 'propagation_update')
      ? 'task' : 'stage';
  const failed = rows.filter((row) => row.task?.status === 'error').length;
  const retrying = selecting && failed > 0;
  const running = rows.some((row) => !row.snapshotAt
    && ['queued', 'ai_in_progress', 'human_in_progress'].includes(row.task?.status ?? ''));
  const proposed = message.savedPlan || (!selecting && (message.proposed || rows.every((row) => !row.task)));
  const collapsed = message.archived || proposed;
  const context = proposed ? 'Proposed tasks' : message.archived ? 'Previous attempt'
    : selecting ? `Choose tasks to ${retrying ? 'retry' : 'run'}` : running ? 'Current attempt' : 'Planning tasks';
  const heading = message.heading.replace(/^Planning tasks/, context);
  const totals = proposed ? `${rows.length} planned ${rows.length === 1 ? 'task' : 'tasks'}`
    : selecting ? `${selectedCount}/${unitCount} ${selectionUnit}${unitCount === 1 ? '' : 's'} selected to ${retrying ? 'retry' : 'run'}`
      : `${completed}/${rows.length} done${failed ? ` · ${failed} failed` : ''}`;
  const checklist = (
    <>
        <TaskChecklist ariaLabel="Planning tasks for this action" heading={collapsed ? 'Task details' : heading}
          completionOnly={!selecting}
          headingAction={
            <button type="button" className="app-action-btn planning-action-tasks__view-all" onClick={viewAllTasks}>
              <i className="fa-solid fa-list-check" aria-hidden="true" />
              View all tasks
            </button>
          }
          totals={collapsed ? null : isLoading ? 'Loading…' : totals} onSelectionChange={toggle}
          items={checklistRows.map(({
            id, title, task, selectable, checked, indeterminate, selectionHint, snapshotAt,
          }) => ({
            id, title, status: task?.status ?? 'backlog',
            statusLabel: snapshotAt && ['queued', 'ai_in_progress', 'human_in_progress'].includes(task?.status ?? '')
              ? `Was ${stateLabel(task?.status).toLowerCase()}`
              : message.archived && task?.status === 'error' ? 'Failed'
                : task?.planning_metadata?.awaiting_confirmation ? 'Confirm name'
                  : selecting && selectionHint ? 'Required' : stateLabel(task?.status),
            statusTitle: selectionHint || task?.execution_step,
            viewable: Boolean(task), selectable: selecting && selectable, checked, indeterminate,
            selectionDependency: selecting && Boolean(selectionHint),
            completionOnly: !selecting || !selectable,
            activityFrozen: Boolean(snapshotAt),
          }))}
          onViewTask={(itemId) => openArtifactDisplay({ tab: 'tasks', itemId, source: 'timeline' })} />
        <PlanningTaskStageStatus tasks={message.rows.flatMap((row) => {
          if (!row.task) return [];

          return row.snapshotAt && ['queued', 'ai_in_progress', 'human_in_progress'].includes(row.task.status)
            ? [{ ...row.task, status: 'paused' as const, execution_step: `Phase ended while ${stateLabel(row.task.status).toLowerCase()}` }]
            : [row.task];
        })} />
    </>
  );

  return (
    <section className="ai-overview-bubble ai-overview-action-entry task-selection-bubble planning-action-tasks">
      <div className="ai-overview-bubble__header">
        <MessageIdentityLabel className="ai-overview-bubble__label" name="Tech Lead Agent"
          timestamp={createdAt ? formatOverviewDateTime(createdAt) : undefined} />
      </div>
      <div className="ai-overview-action-entry__body">
        {collapsed ? (
          <details className="planning-action-tasks__history">
            <summary>
              <span className="planning-action-tasks__history-heading">{heading}</span>
              <span className="planning-action-tasks__history-totals">{totals}</span>
              <i className="fa-solid fa-chevron-down" aria-hidden="true" />
            </summary>
            {checklist}
          </details>
        ) : checklist}
      </div>
    </section>
  );
};
